/*
 * ironstone-lines.js (V10, 2026-11-02)
 * Behaviour for the lines of work and the service pages. Loaded with defer by
 * snippets/lines-runtime.liquid. Panels needs no script: pointing at a room
 * opens it in CSS.
 *
 * Everything initialises per root: once for the page, and again for any
 * section the theme editor redraws (shopify:section:load), which it does on
 * every setting or block change.
 *
 * The scroll reveal is opted into HERE, not in the head: whatever is already
 * on screen is marked shown first, and only then does html.il-anim hide what
 * waits below the fold. A script that never loads therefore hides nothing,
 * and in the theme editor nothing is ever hidden.
 */
(() => {
  const $$ = (s, r = document) => [...r.querySelectorAll(s)];
  const html = document.documentElement;
  const motionOK = !matchMedia('(prefers-reduced-motion: reduce)').matches;
  const hasIO = 'IntersectionObserver' in window;
  const reveal = motionOK && hasIO && !html.classList.contains('shopify-design-mode');
  const rvIO = reveal ? new IntersectionObserver(entries => entries.forEach(e => {
    if (e.isIntersecting) { e.target.classList.add('is-in'); rvIO.unobserve(e.target); }
  }), { rootMargin: '0px 0px -8% 0px' }) : null;

  function initReveal(root) {
    $$('.il-rv', root).forEach(el => {
      if (!reveal || el.getBoundingClientRect().top < innerHeight) el.classList.add('is-in');
      else rvIO.observe(el);
    });
  }

  // The film never autoplays for a visitor who asked for less motion.
  function initFilm(root) {
    if (motionOK) return;
    $$('[data-il-film]', root).forEach(v => { v.removeAttribute('autoplay'); v.pause(); });
  }

  // Beside: pointing at a line shows its object in the film's frame. A line
  // with no studio image leaves the film showing rather than a white frame.
  function initBeside(root) {
    $$('[data-il-beside]', root).forEach(b => {
      const viewer = b.querySelector('[data-il-viewer]');
      const list = b.querySelector('[data-il-list]');
      if (!viewer || !list) return;
      const show = id => {
        const objs = $$('[data-il-for]', viewer);
        const match = id ? objs.find(o => o.dataset.ilFor === id) : null;
        viewer.classList.toggle('is-on', !!match);
        objs.forEach(o => o.classList.toggle('is-on', o === match));
      };
      const pick = e => { const a = e.target.closest('[data-il-line]'); if (a) show(a.dataset.ilLine); };
      list.addEventListener('mouseover', pick);
      list.addEventListener('focusin', pick);
      list.addEventListener('mouseleave', () => show(null));
      list.addEventListener('focusout', e => { if (!list.contains(e.relatedTarget)) show(null); });
    });
  }

  // Stage: the object belongs to whichever line is crossing the middle of the screen.
  function initStage(root) {
    $$('[data-il-stage-root]', root).forEach(s => {
      const frame = s.querySelector('[data-il-frame]');
      const items = $$('[data-il-stage]', s);
      if (!frame || !items.length) return;
      const to = id => {
        $$('[data-il-for]', frame).forEach(o => o.classList.toggle('is-on', o.dataset.ilFor === id));
        items.forEach(i => i.classList.toggle('is-active', i.dataset.ilStage === id));
      };
      to(items[0].dataset.ilStage);
      if (!hasIO) return;
      const io = new IntersectionObserver(entries => entries.forEach(e => {
        if (e.isIntersecting) to(e.target.dataset.ilStage);
      }), { rootMargin: '-45% 0px -45% 0px' });
      items.forEach(i => io.observe(i));
    });
  }

  // The work: filter chips, and a lightbox that steps through what the filter shows.
  function initWork(root) {
    $$('[data-il-work]', root).forEach(w => {
      const figs = $$('[data-il-tag]', w);
      const chips = $$('[data-il-filter]', w);
      const lb = w.querySelector('[data-il-lb]');
      let shown = figs, at = 0, opener = null;

      chips.forEach(chip => chip.addEventListener('click', () => {
        const tag = chip.dataset.ilFilter;
        chips.forEach(c => c.setAttribute('aria-pressed', String(c === chip)));
        figs.forEach(f => { f.hidden = tag !== 'all' && f.dataset.ilTag !== tag; });
        shown = figs.filter(f => !f.hidden);
      }));

      if (!lb || typeof lb.showModal !== 'function') return;
      const img = lb.querySelector('[data-il-lb-img]');
      const cap = lb.querySelector('[data-il-lb-cap]');
      const steps = $$('[data-il-lb-step]', lb);
      const show = i => {
        at = (i + shown.length) % shown.length;
        const btn = shown[at].querySelector('[data-il-lb-open]');
        img.src = btn.dataset.full;
        img.alt = btn.dataset.caption || '';
        cap.textContent = btn.dataset.caption || '';
        steps.forEach(s => { s.hidden = shown.length < 2; });
      };
      w.addEventListener('click', e => {
        const open = e.target.closest('[data-il-lb-open]');
        if (!open) return;
        opener = open;
        show(shown.indexOf(open.closest('[data-il-tag]')));
        lb.showModal();
      });
      steps.forEach(s => s.addEventListener('click', () => show(at + Number(s.dataset.ilLbStep))));
      lb.querySelector('[data-il-lb-close]').addEventListener('click', () => lb.close());
      lb.addEventListener('click', e => { if (e.target === lb) lb.close(); });
      lb.addEventListener('keydown', e => {
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') show(at + (e.key === 'ArrowRight' ? 1 : -1));
      });
      lb.addEventListener('close', () => { if (opener) opener.focus(); });
    });
  }

  function init(root) {
    initReveal(root);
    initFilm(root);
    initBeside(root);
    initStage(root);
    initWork(root);
  }

  init(document);
  if (reveal) html.classList.add('il-anim');
  document.addEventListener('shopify:section:load', e => init(e.target));
})();
